import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  HelpTip,
  OptionsIcon,
  ArrowLeftTopIcon,
  Vertical,
  Horizontal,
} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import FlightCheckIssue from './FlightCheckIssue';
import {useSelector} from 'react-redux';
import {useUserFlowState} from '../../Context/UserFlow/UserFlowContext';
import {UserFlowStep} from '../../../Facade/UserFlow/BaseUserFlow';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {
  FlightCheckError,
  FlightCheckModel,
} from '@taopix/taopix-core/dist/types/Reducers/FlightCheckReducer';
import {
  CollectionType,
  CoreDoms,
  Lang,
  Localisation,
  NumBool,
  PageConstants,
  Viewers,
  ViewMode,
} from '@taopix/taopix-core';
import ToolPaletteSection, {ToolPaletteSectionProps} from '../../ToolPalette/ToolPaletteSection';
import {UserFlowType} from '../../../Facade/UserFlow/UserFlowFacade';
import {PagesSelector} from '../../../Selectors';
import PaletteInformationMessage from '../BoxSettings/PaletteInformationMessage';
import {useExperience} from '../../Experience/ExperienceProvider';
import useViewMode from '../../../Hooks/useViewMode';
import {useTaopix} from '../../Context/TaopixContext';

export type FlightCheckErrorData = {
  errorFlag: number;
  errorMessage: string;
  infoString: string;
  pageNumber: number;
  realPageNumber?: number;
  label?: string;
};

export type FlightCheckProps = {
  sectionKey: string;
  item: ComputedPageItem;
  sectionProps?: Partial<ToolPaletteSectionProps>;
};

const FlightCheck = ({sectionKey, item, sectionProps}: FlightCheckProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {experience} = useExperience();
  const [viewMode, setViewMode] = useViewMode();
  const {t} = useTranslation(['flightcheckerrors', 'flightcheck']);
  const userFlow = useUserFlowState();

  const stateManager = window.Taopix.Core.get('stateManager');
  const productLayout = stateManager.getLayout();
  const pages = useSelector(PagesSelector);

  const [showButton, setShowButton] = useState<boolean>(false);
  const [introTipOpen, setIntroTipOpen] = useState<boolean>(false);
  const flightCheckIssueStepActivated = useRef<boolean>(false);

  const paletteOpen = useMemo(
    () => ui.state.palette.section?.includes(sectionKey) || false,
    [ui.state.palette.section]
  );

  useEffect(() => {
    if (paletteOpen) {
      setIntroTipOpen(true);
    } else {
      setIntroTipOpen(false);
    }
  }, [paletteOpen]);

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'pb-lg',
    'space-y-sm',
    'overflow-y-auto',
    'px-md',
    'lg:flex-1',
    'sm:h-[260px]'
  );

  const infoStrings = t('infoStrings', {returnObjects: true, ns: 'flightcheckerrors'});
  const warnings = t('warnings', {returnObjects: true, ns: 'flightcheckerrors'});

  const errors: FlightCheckModel = useSelector<any>(state => state?.flightcheck) as any;

  const mappedErrorsCritical: FlightCheckError = {...errors?.criticalErrors};
  const mappedErrorsMinor: FlightCheckError = {...errors?.minorErrors};

  for (const key in errors?.minorErrors) {
    const error: FlightCheckErrorData = errors?.minorErrors[key];
    let errorMessage: string;
    if (error.label && '' !== error.label) {
      errorMessage = warnings[error.errorMessage].replace('^0', error.label);
    } else {
      errorMessage = warnings[error.errorMessage];
    }
    const infoString: string = infoStrings[error.infoString];
    mappedErrorsMinor[key] = {...error, errorMessage, infoString};
  }

  for (const key in errors?.criticalErrors) {
    const error: FlightCheckErrorData = errors?.criticalErrors[key];
    let errorMessage: string;
    if (error.label && '' !== error.label) {
      errorMessage = warnings[error.errorMessage].replace('^0', error.label);
    } else {
      errorMessage = warnings[error.errorMessage];
    }
    const infoString: string = infoStrings[error.infoString];
    mappedErrorsCritical[key] = {...error, errorMessage, infoString};
  }

  const sortErrors = (errors: FlightCheckError) => {
    return Object.values(errors).sort((a, b) => {
      if (a.pageNumber === PageConstants.FrontCoverPageNumber) {
        return -1;
      } else if (b.pageNumber === PageConstants.FrontCoverPageNumber) {
        return 1;
      } else if (a.pageNumber === PageConstants.BackCoverPageNumber) {
        return 1;
      } else if (b.pageNumber === PageConstants.BackCoverPageNumber) {
        return -1;
      } else {
        return a.pageNumber - b.pageNumber;
      }
    });
  };

  const sortedMinorErrors = sortErrors(mappedErrorsMinor);
  const sortedCriticalErrors = sortErrors(mappedErrorsCritical);

  const flightCheckIssueFixStepActive = userFlow.isStepActive(
    UserFlowType.FlightCheck,
    UserFlowStep.FlightCheckIssueFix
  );

  const errorCount = useMemo(() => {
    return Object.keys(mappedErrorsCritical).length + Object.keys(mappedErrorsMinor).length;
  }, [Object.keys(mappedErrorsCritical).length, Object.keys(mappedErrorsMinor).length]);

  useEffect(() => {
    if (flightCheckIssueFixStepActive && !ui.state.isSmallScreen) {
      flightCheckIssueStepActivated.current = true;
      if (errorCount > 0) {
        ui.dispatch(UserInterfaceActions.setToolPaletteSection(sectionKey));
      } else {
        flightCheckIssueStepActivated.current = false;
        setShowButton(false);
      }
    } else {
      setShowButton(false);
    }
  }, [ui.state.isSmallScreen, flightCheckIssueFixStepActive]);

  // This will return the pagenumber which will allow for the getting of the page from the store
  const getCorrectPageNumber = (pagenumber: number): number => {
    if (
      [PageConstants.FrontCoverPageNumber, PageConstants.BackCoverPageNumber].includes(pagenumber)
    )
      return pagenumber;
    return NumBool.True === productLayout.spreads
      ? 0 === pagenumber % 2
        ? pagenumber
        : pagenumber - 1
      : pagenumber;
  };

  const onClickFlightCheckIssue = (error: FlightCheckErrorData) => {
    let pagenumber = error.realPageNumber as number;
    if (
      productLayout.spreads === 1 &&
      pagenumber % 2 !== 0 &&
      PageConstants.BackCoverPageNumber !== pagenumber
    ) {
      pagenumber -= 1;
    }

    // if in grid view switch to either page/list view. Try defaultView first otherwise fall back on whichever is visible.
    if (ViewMode.Grid === viewMode) {
      if (experience.EDITOR.workArea.defaultView === 'ls') {
        setViewMode(ViewMode.List);
      } else if (experience.EDITOR.workArea.defaultView === 'spv') {
        setViewMode(ViewMode.Single);
      } else if (experience.EDITOR.workArea.editingViewModes.list) {
        setViewMode(ViewMode.List);
      } else if (experience.EDITOR.workArea.editingViewModes.page) {
        setViewMode(ViewMode.Single);
      }
      app.core.get('abilities').setPage(pagenumber).then();
      return;
    }

    document
      .querySelector(`#${CoreDoms.ViewportName}-${Viewers.Primary}-${pagenumber}`)
      ?.scrollIntoView({behavior: 'auto', block: 'center', inline: 'nearest'});
  };

  // get array of left pagenumbers from the pages in the redux store
  const pageNumbers = pages.map(page => {
    return page.pagenumber;
  });

  // If a cover page has a predefinded name then use that otherwise fall back to the name
  const getPageName = (error: FlightCheckErrorData): string => {
    const correctedPageNumber = getCorrectPageNumber(error.realPageNumber as number);
    const predefinedName = stateManager.getPage(correctedPageNumber)?.predefinedname;
    const name = stateManager.getPage(Number(error.realPageNumber))?.name;
    let pageName;
    let coverName;

    if (predefinedName) {
      pageName = predefinedName;
      coverName = predefinedName;
    } else if (name) {
      coverName = name;
    }

    if (PageConstants.FrontCoverPageNumber === (error.realPageNumber as number)) {
      return coverName
        ? Localisation.getLocaleString(Lang.getCode(), coverName)
        : t('*:str_LabelCover');
    }
    if (PageConstants.BackCoverPageNumber === (error.realPageNumber as number)) {
      return coverName
        ? Localisation.getLocaleString(Lang.getCode(), coverName)
        : t('*:str_LabelBackCover');
    }

    if (pageName) {
      if (NumBool.True === productLayout.spreads) {
        // if the realPageNumber is included in the pageNumbers array then this is the left side of the spread
        if (pageNumbers.includes(error.realPageNumber as number)) {
          return `${Localisation.getLocaleString(Lang.getCode(), pageName)} ${t(
            'flightcheck:str_LabelLeftSide'
          )}`;
        } else {
          return `${Localisation.getLocaleString(Lang.getCode(), pageName)} ${t(
            'flightcheck:str_LabelRightSide'
          )}`;
        }
      } else {
        const localePageName = Localisation.getLocaleString(Lang.getCode(), pageName);
        // Capitilise the month name for a calendar
        if (productLayout.type === CollectionType.Calendar) {
          return localePageName.charAt(0).toUpperCase() + localePageName.slice(1);
        }
        return localePageName;
      }
    } else {
      return `${t('*:str_LabelPageName').replace('^0', `${error.pageNumber}`)}`;
    }
  };

  return (
    <ToolPaletteSection
      sectionKey={sectionKey}
      useFocusMode
      title={t('flightcheck:str_TitleFinalChecks')}
      buttonIcon={<OptionsIcon />}
      badgeContent={String(errorCount)}
      showButton={showButton || (!ui.state.isSmallScreen && flightCheckIssueStepActivated.current)}
      {...sectionProps}
    >
      <div className={wrapperClasses} id="flight-check-section">
        {errorCount === 0 ? (
          <PaletteInformationMessage
            message={t('flightcheck:str_MessageFlightcheckNoErrorsDetected')}
          />
        ) : (
          <>
            {sortedCriticalErrors &&
              sortedCriticalErrors.map((error: any, index) => {
                return (
                  <FlightCheckIssue
                    key={`critical-error-${error.ref}-${index}`}
                    type={'critical'}
                    issueTitle={error.errorMessage}
                    issueDescription={error.infoString}
                    pageName={getPageName(error)}
                    onClick={() => {
                      onClickFlightCheckIssue(error);
                    }}
                  />
                );
              })}
            {sortedMinorErrors &&
              sortedMinorErrors.map((error: any, index: number) => {
                return (
                  <FlightCheckIssue
                    key={`warning-error-${error}-${index}`}
                    type={'warning'}
                    issueTitle={error.errorMessage}
                    issueDescription={error.infoString}
                    pageName={getPageName(error)}
                    onClick={() => {
                      onClickFlightCheckIssue(error);
                    }}
                  />
                );
              })}
          </>
        )}
      </div>
      <HelpTip
        open={!ui.state.isSmallScreen && introTipOpen}
        id={'FlightCheckReviewHelpTip-lg'}
        message={t('flightcheck:str_MessageReviewIssueLargeScreen')}
        buttonLabel={t('*:str_ButtonOK')}
        buttonFunction={() => setIntroTipOpen(false)}
        anchorId={'flight-check-section'}
        anchorOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Right}}
        transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Left}}
        offset={{x: 10, y: 0}}
        icon={<ArrowLeftTopIcon />}
        className={'max-w-[280px]'}
        showAfter={500}
        showMode={'once'}
      />
    </ToolPaletteSection>
  );
};

export default FlightCheck;
